/**
 * "Open points (n)": everything that stands between a case and a reply, as one list
 * (ease round, 13 Oct 2026: the page named the first step only, and a new person did not
 * see what else a case needed, or that I2Q could not finish it).
 *
 * The server sends the list on the case (`blockers`). The checklist shows it in two
 * parts: "Before you can prepare the reply" (the points that hold a reply) and "Open in
 * the reply if you leave them" (the reply then lists them as open). A row is the icon of
 * its kind from the one icon map, the sentence as a link to its control, and at most the
 * buttons that resolve it in one click. When the data is not in F&O (no account, no item,
 * no valid price), the data gap block says so and offers the two ways out: set it by hand
 * (the control the sentence points at), or set the status of the case.
 *
 * `BlockerList` and `DataGapBlock` render without the popover, so the bar can show the
 * data gap under its caption and a test can read the list.
 *
 * After the review of the round (13 Oct 2026): the list of the server is as old as the
 * last read of the case, so a button that writes many lines reads the rows of the page,
 * not the keys of the blocker. "Set 1 for all" is gone since 14 Oct 2026: a line the mail
 * gave no quantity for has the default 1 (product owner). No button writes many lines
 * while a colleague has the case. The data gap block
 * speaks for the whole case, so a gap on some of the lines only does not show it: the
 * sentence of that blocker already names the way out per line.
 */

import { Icon, type IconComponent } from "@norican/icons";
import { Button, Popover, Typography } from "@norican/react";
import { useState } from "react";
import { Link } from "react-router";

import { quotableRows } from "@/features/case-review/bulk-quantity-dialog";
import {
  blockerSentence,
  type BlockerTarget,
  blockerTargetOf,
  mustBlockers,
  needsOneByOne,
  openPoints,
  rowNumberOf,
} from "@/features/case-review/case-action";
import { groupedRows } from "@/features/case-review/draft-line-groups";
import { type CaseWrites, colleagueHasCase } from "@/features/case-review/use-case-writes";
import { HELP_TOPICS } from "@/features/help";
import { type CaseBlocker } from "@/lib/api";
import {
  CUSTOMER_ICON,
  PRICE_ICON,
  QTY_ISSUE_ICON,
  SOURCE_ICON,
  STATUS_ICON,
} from "@/lib/line-icons";
import { logger } from "@/lib/logger";
import { type QuoteCaseDetail } from "@/lib/mock";
import {
  ACTION_LABELS,
  CHECKLIST_WORDS,
  customerMissingText,
  DATA_GAP_WORDS,
  LINE_STATUS_WORDS,
} from "@/lib/sales-flow";

/** The icon of a blocker kind, from the one icon map (`lib/line-icons.ts`). */
export const BLOCKER_ICON: Record<CaseBlocker["kind"], IconComponent> = {
  customer_missing: CUSTOMER_ICON.none,
  customer_to_confirm: CUSTOMER_ICON.suggested,
  no_lines: STATUS_ICON[LINE_STATUS_WORDS.pick],
  lines_to_pick: STATUS_ICON[LINE_STATUS_WORDS.pick],
  lines_to_confirm: STATUS_ICON[LINE_STATUS_WORDS.confirm],
  lines_to_accept: STATUS_ICON[LINE_STATUS_WORDS.matched],
  price_held: PRICE_ICON.below_margin,
  rule_hold: PRICE_ICON.below_margin,
  quantity_missing: QTY_ISSUE_ICON,
  quantity_to_check: QTY_ISSUE_ICON,
  price_missing: PRICE_ICON.none,
  // Hunt round 2 (Q-18): a price account or a discount instruction of the customer.
  customer_instruction: CUSTOMER_ICON.certain,
  // Decision D3 (19 Oct 2026): a long list on a picture was read in part.
  picture_cut: SOURCE_ICON.photo,
};

/** Why I2Q cannot fill the gap of a blocker, in the words of record; null for another kind. */
export function dataGapReason(blocker: CaseBlocker): string | null {
  if (!blocker.data_gap) {
    return null;
  }
  switch (blocker.kind) {
    case "customer_missing":
      // Hunt round 2 (Q-14): the sender's, the agent's or the end customer's account.
      return customerMissingText(blocker.party);
    case "lines_to_pick":
      return DATA_GAP_WORDS.lines_to_pick;
    case "price_missing":
      return DATA_GAP_WORDS.price_missing;
    default:
      return null;
  }
}

/** The rows of the draft as the page lists them now (stand-in lines left out). */
function draftRowsOf(detail: QuoteCaseDetail) {
  return groupedRows(
    detail.lines.filter((line) => !line.fallback),
    detail.lines,
  );
}

/**
 * True when the data gap of a blocker is the gap of the whole case: no account, or no
 * item or no valid price on every line to quote. The block then says "I2Q cannot finish
 * this case by itself" and offers to close the case. One line of 69 without an item is
 * not that: its sentence says "pick an item or press No item" (review of the ease round,
 * 13 Oct 2026). Without `detail` only the word of the server counts.
 */
export function isCaseDataGap(blocker: CaseBlocker, detail?: QuoteCaseDetail): boolean {
  if (dataGapReason(blocker) === null) {
    return false;
  }
  if (blocker.kind === "customer_missing" || !detail) {
    return true;
  }
  const listed =
    blocker.count && blocker.count > 0 ? blocker.count : (blocker.line_indexes ?? []).length;
  return listed >= quotableRows(draftRowsOf(detail)).length;
}

/** The first blocker whose data is not in F&O for the whole case, or null. */
export function firstDataGap(
  blockers: readonly CaseBlocker[] | null | undefined,
  detail?: QuoteCaseDetail,
) {
  return (blockers ?? []).find((blocker) => isCaseDataGap(blocker, detail)) ?? null;
}

/** The Help topic the data gap block links to. */
export const DATA_GAP_HELP_TOPIC = "case-does-not-go-through";

/** "Help: When a case does not go through", the words of the link. */
export function dataGapHelpText(): string {
  const topic = HELP_TOPICS.find((entry) => entry.id === DATA_GAP_HELP_TOPIC);
  return `Help: ${topic?.title ?? "When a case does not go through"}`;
}

/** The first line of a blocker: its index in `detail.lines`, its id and its key. */
function firstLineOf(blocker: CaseBlocker, detail: QuoteCaseDetail) {
  const lineIndex = blocker.line_indexes?.[0];
  const line = typeof lineIndex === "number" ? detail.lines[lineIndex] : undefined;
  return line && typeof lineIndex === "number" ? { line, lineIndex } : null;
}

/** The keys of the lines a blocker lists (the server sends them next to the indexes). */
function lineKeysOf(blocker: CaseBlocker, detail: QuoteCaseDetail): string[] {
  // A line the bulk actions skip (hunt round 2: an item that does not fit the row, a scan
  // line one reading gave) is confirmed one by one, never by "Confirm n items".
  const oneByOne = new Set(
    detail.lines
      .filter((line) => needsOneByOne(line))
      .map((line) => line.lineKey ?? "")
      .filter(Boolean),
  );
  const keys =
    blocker.line_keys && blocker.line_keys.length > 0
      ? blocker.line_keys
      : (blocker.line_indexes ?? [])
          .map((index) => detail.lines[index]?.lineKey ?? "")
          .filter((key) => key !== "");
  return keys.filter((key) => !oneByOne.has(key));
}

export interface BlockerActions {
  detail: QuoteCaseDetail;
  writes: CaseWrites;
  /** The viewer may approve a price below the limit. */
  mayApprove: boolean;
  /** The name of the account a one-click confirmation offers, as the button says it. */
  customerName: string | null;
  /** A sentence was clicked: go to its control. */
  onTarget: (target: BlockerTarget) => void;
  /** Opens "Set quantity for all lines". */
  onSetQuantityAll: () => void;
  /** Opens "Set price" for a line. */
  onSetPrice?: (lineId: string) => void;
  /** Runs after a click that leaves the list (the popover closes). */
  onDone?: () => void;
}

/**
 * The data gap block: I2Q cannot finish the case, why, and the two statuses that close
 * it in one click. "By hand" is the control the sentence over it points at.
 */
export function DataGapBlock({
  blocker,
  writes,
  onClosed,
}: {
  blocker: CaseBlocker;
  writes: CaseWrites;
  /** Runs after "Not for us" was saved (the queue walk goes to the next case). */
  onClosed?: () => void;
}) {
  const reason = dataGapReason(blocker);
  if (!reason) {
    return null;
  }
  return (
    <div className="flex min-w-0 flex-col gap-1.5" data-data-gap={blocker.kind}>
      <Typography variant="caption" className="text-fg break-words">
        <span className="font-semibold">{DATA_GAP_WORDS.lead}</span> {reason} {DATA_GAP_WORDS.ways}
      </Typography>
      <span className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          tone="neutral"
          disabled={writes.busy}
          onClick={() => {
            logger.debug("[case] data gap", { kind: blocker.kind, status: "not_for_us" });
            writes.setStatus("not_for_us", onClosed);
          }}
        >
          {ACTION_LABELS.notForUs}
        </Button>
        <Button
          size="sm"
          variant="outline"
          tone="neutral"
          disabled={writes.busy}
          onClick={() => {
            logger.debug("[case] data gap", { kind: blocker.kind, status: "waiting_customer" });
            writes.setStatus("waiting_customer");
          }}
        >
          {ACTION_LABELS.waitingCustomer}
        </Button>
        {/* The Help part "When a case does not go through", in a new tab: the case stays. */}
        <Link
          to={`/help#${DATA_GAP_HELP_TOPIC}`}
          target="_blank"
          rel="noreferrer"
          className="text-accent-fg-subtle text-xs font-semibold hover:underline"
          data-data-gap-help
        >
          {dataGapHelpText()}
        </Link>
      </span>
    </div>
  );
}

/** The buttons of one row: what resolves the blocker in one click, or opens its dialog. */
function BlockerButtons({ blocker, actions }: { blocker: CaseBlocker; actions: BlockerActions }) {
  const { detail, writes, mayApprove, customerName, onTarget, onSetQuantityAll, onSetPrice } =
    actions;
  const done = actions.onDone ?? (() => undefined);
  const first = firstLineOf(blocker, detail);
  const count =
    blocker.count && blocker.count > 0 ? blocker.count : lineKeysOf(blocker, detail).length;
  // A colleague has the case: no button here writes many lines into it. "Take over" is
  // the big button; one row can still be corrected in the draft.
  const bulkOff = colleagueHasCase(writes);
  const button = (label: string, run: () => void, mark?: string) => (
    <Button
      size="sm"
      variant="outline"
      tone="accent"
      disabled={writes.busy}
      onClick={() => {
        logger.debug("[case] open point", { case: detail.id, kind: blocker.kind, button: label });
        run();
        done();
      }}
      data-blocker-button={mark ?? blocker.kind}
    >
      {label}
    </Button>
  );
  switch (blocker.kind) {
    case "customer_missing":
      return button(ACTION_LABELS.findCustomer, () => onTarget({ kind: "customer" }));
    case "customer_to_confirm":
      return blocker.one_click && blocker.account
        ? button(ACTION_LABELS.confirmCustomer(customerName ?? blocker.account), () =>
            writes.confirmCustomer(blocker.account ?? null),
          )
        : button(ACTION_LABELS.chooseCustomer, () => onTarget({ kind: "customer" }));
    case "no_lines":
      return button(ACTION_LABELS.addLine, () => onTarget({ kind: "add_line" }));
    case "lines_to_confirm": {
      // The button counts what it confirms: a line checked one by one is not in it.
      const keys = lineKeysOf(blocker, detail);
      return bulkOff || keys.length === 0
        ? null
        : button(ACTION_LABELS.confirmLines(keys.length), () => writes.confirmSuggested(keys));
    }
    case "lines_to_accept":
      return bulkOff ? null : button(ACTION_LABELS.accept(count), () => writes.acceptMatched());
    case "price_held":
      if (!first) {
        return null;
      }
      return mayApprove
        ? button(ACTION_LABELS.approvePrice, () => writes.approvePrice(first.lineIndex, true))
        : onSetPrice
          ? button(ACTION_LABELS.changePrice, () => onSetPrice(first.line.id))
          : null;
    case "quantity_missing":
      // A line the mail gave no quantity for has the default 1 (14 Oct 2026) and is no
      // such point; what is left (a reference line, a 0) gets the dialog, never a 1.
      return bulkOff ? null : button(ACTION_LABELS.setQuantityAll, onSetQuantityAll);
    case "price_missing":
      return first && onSetPrice
        ? button(ACTION_LABELS.setPrice, () => onSetPrice(first.line.id))
        : null;
    case "lines_to_pick":
    case "rule_hold":
    case "quantity_to_check":
    case "customer_instruction":
    case "picture_cut":
      // The link goes to the first row, to the rule under "Checks", to the customer, or
      // to the picture in the documents (D3: no button reads the rest of a list).
      return null;
  }
}

function BlockerRow({ blocker, actions }: { blocker: CaseBlocker; actions: BlockerActions }) {
  const { detail, mayApprove, customerName, onTarget } = actions;
  const sentence = blockerSentence(blocker, {
    mayApprove,
    rowOf: (lineIndex) => rowNumberOf(detail, lineIndex),
    customerName,
  });
  return (
    <li
      className="flex min-w-0 flex-wrap items-start gap-x-2 gap-y-1"
      data-blocker={blocker.kind}
      data-blocker-level={blocker.level}
    >
      <Icon
        icon={BLOCKER_ICON[blocker.kind]}
        size="xs"
        className="text-fg-subtle mt-1.5 shrink-0"
      />
      <Button
        size="sm"
        variant="ghost"
        tone="neutral"
        className="h-auto min-w-0 flex-1 justify-start px-1 py-1 text-left font-normal whitespace-normal"
        onClick={() => {
          onTarget(blockerTargetOf(blocker, detail));
          actions.onDone?.();
        }}
      >
        <span className="text-fg min-w-0 text-sm break-words">{sentence}</span>
      </Button>
      <span className="flex flex-wrap items-center gap-1.5">
        <BlockerButtons blocker={blocker} actions={actions} />
      </span>
    </li>
  );
}

/**
 * The list on its own: the two titles with their rows, then the data gap block. Renders
 * nothing for an empty list.
 */
export function BlockerList(actions: BlockerActions & { onClosed?: () => void }) {
  const blockers = actions.detail.blockers ?? [];
  const must = mustBlockers(blockers);
  const open = openPoints(blockers);
  const gap = firstDataGap(blockers, actions.detail);
  if (blockers.length === 0) {
    return null;
  }
  const part = (title: string, rows: readonly CaseBlocker[], level: string) =>
    rows.length > 0 && (
      <section className="flex min-w-0 flex-col gap-1" data-checklist-part={level}>
        <Typography variant="label" className="text-fg">
          {title}
        </Typography>
        <ul className="flex min-w-0 flex-col gap-1">
          {rows.map((blocker, index) => (
            <BlockerRow
              key={`${blocker.kind}-${blocker.rule_id ?? ""}-${index}`}
              blocker={blocker}
              actions={actions}
            />
          ))}
        </ul>
      </section>
    );
  return (
    <div className="flex min-w-0 flex-col gap-3" data-blocker-checklist>
      {part(CHECKLIST_WORDS.must, must, "must")}
      {part(CHECKLIST_WORDS.open, open, "open_point")}
      {gap && (
        <div className="border-border-subtle border-t pt-2">
          <DataGapBlock
            blocker={gap}
            writes={actions.writes}
            onClosed={() => {
              actions.onClosed?.();
              actions.onDone?.();
            }}
          />
        </div>
      )}
    </div>
  );
}

export interface BlockerChecklistProps extends Omit<BlockerActions, "onDone"> {
  /** Runs after "Not for us" was saved from the data gap block. */
  onClosed?: () => void;
}

/**
 * "Open points (n)" in the action bar, with the list in a popover over the bar. `n`
 * counts both parts. Renders nothing when the case sends no list or an empty one.
 */
export function BlockerChecklist(props: BlockerChecklistProps) {
  const [open, setOpen] = useState(false);
  const count = props.detail.blockers?.length ?? 0;
  if (count === 0) {
    return null;
  }
  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        logger.debug("[case] open points", { case: props.detail.id, open: next, count });
        setOpen(next);
      }}
    >
      <Popover.Trigger
        render={<Button size="sm" variant="outline" tone="neutral" data-open-points={count} />}
      >
        {ACTION_LABELS.openPoints(count)}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner side="top" align="start" sideOffset={8}>
          <Popover.Popup className="max-h-[70vh] w-[min(32rem,calc(100vw-2rem))] overflow-y-auto">
            <Popover.Header>
              <Popover.Title>{ACTION_LABELS.openPoints(count)}</Popover.Title>
              <Popover.CloseButton label="Close" />
            </Popover.Header>
            <Popover.Body>
              <BlockerList {...props} onDone={() => setOpen(false)} />
            </Popover.Body>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
